export type TaggedTemplate = {
  name?: string;
  description?: string | null;
  tags?: string[] | null;
  system_name?: string | null;
  expert_type_display?: string | null;
};

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9+#/._-]+/g, ' ')
    .trim();

export const normalizeTags = (tags: Array<string | null | undefined> = []): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  tags.forEach((rawTag) => {
    const tag = String(rawTag ?? '').replace(/^#+/, '').replace(/\s+/g, ' ').trim();
    if (!tag) return;
    const key = tag.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    normalized.push(tag.slice(0, 64));
  });

  return normalized.slice(0, 20);
};

const tokenize = (query: string): string[] => {
  const tokens = normalizeText(query)
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  return Array.from(new Set(tokens));
};

export const scoreTemplateSimilarity = (template: TaggedTemplate, query: string): number => {
  const tokens = tokenize(query);
  if (!tokens.length) return 0;

  const name = normalizeText(template.name);
  const description = normalizeText(template.description);
  const systemName = normalizeText(template.system_name);
  const expertType = normalizeText(template.expert_type_display);
  const tags = normalizeTags(template.tags || []).map(normalizeText);
  const allText = [name, description, systemName, expertType, tags.join(' ')].join(' ');

  return tokens.reduce((score, token) => {
    if (tags.some((tag) => tag === token)) score += 10;
    if (tags.some((tag) => tag.includes(token) && tag !== token)) score += 7;
    if (name.includes(token)) score += 5;
    if (systemName.includes(token)) score += 4;
    if (expertType.includes(token)) score += 3;
    if (description.includes(token)) score += 2;
    if (allText.includes(token)) score += 1;
    return score;
  }, 0);
};

export const rankTemplatesBySimilarity = <T extends TaggedTemplate>(
  templates: T[],
  query: string,
): T[] => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return templates;

  return templates
    .map((template, index) => ({
      template,
      index,
      score: scoreTemplateSimilarity(template, trimmedQuery),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((item) => item.template);
};
